//////////////////////////////////
// IMPORTING MODULES
//////////////////////////////////

import {
  Globe2,
  Plane,
  BarChart3,
  Palette,
  Map,
  Route,
  Smartphone,
  Cloud,
  Sun,
} from "lucide-react";
import React from "react";

//////////////////////////////////
// COMPONENT: FEATURES
//////////////////////////////////

const Features = (): JSX.Element => {
  const features = [
    {
      icon: <Globe2 className="h-6 w-6" />,
      title: "Interactive 3D Globe",
      description:
        "Rotate, zoom, and explore your travels on a beautifully rendered Earth with real-time controls powered by Three.js.",
    },
    {
      icon: <Plane className="h-6 w-6" />,
      title: "Airport Tracking",
      description:
        "Search and add any airport worldwide using our database with 10,000+ airports indexed by name, city, or IATA code.",
    },
    {
      icon: <Map className="h-6 w-6" />,
      title: "Country Intelligence",
      description:
        "Automatic integration with REST Countries API displaying flags, capitals, population, languages, and currencies for each destination.",
    },
    {
      icon: <BarChart3 className="h-6 w-6" />,
      title: "Travel Statistics",
      description:
        "Track your journey with detailed metrics including total airports visited, countries explored, continents covered, and milestones.",
    },
    {
      icon: <Palette className="h-6 w-6" />,
      title: "Customizable Design",
      description:
        "Personalize your globe with multiple texture options, adjustable pin colors, connection arc colors, and optional gridlines.",
    },
    {
      icon: <Smartphone className="h-6 w-6" />,
      title: "Mobile-First",
      description:
        "Fully responsive interface with landscape mode detection, safe area insets for notched devices, and touch-optimized controls.",
    },
    {
      icon: <Route className="h-6 w-6" />,
      title: "Journey Pathways",
      description:
        "Automatic curved arc generation between consecutive airports showing your travel routes with dynamic rendering optimization.",
    },
    {
      icon: <Cloud className="h-6 w-6" />,
      title: "Cloud Storage",
      description:
        "Secure cloud-based storage via Supabase ensuring your travel history is safely synced across all devices with real-time updates.",
    },
    {
      icon: <Sun className="h-6 w-6" />,
      title: "Dynamic Textures",
      description:
        "Choose between day mesh, night mesh, and no mesh. Soon features with local time-based rendering and real-time timezone shading will be implemented!",
    },
  ];

  return (
    <section id="features" className="py-20 bg-[#141826]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold mb-4">
            Powerful Features
          </h2>
          <p className="text-xl text-gray-400 px-10">
            Everything you need to map your travel journey
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 px-10">
          {features.map((feature, index) => (
            <div
              key={index}
              className="bg-[#1a1f2e]/60 backdrop-blur-sm border border-[#2ab8ff]/20 hover:border-[#2ab8ff]/50 hover:shadow-lg hover:shadow-[#2ab8ff]/10 transition-all hover:-translate-y-2 rounded-xl p-6"
            >
              <div className="w-12 h-12 rounded-full bg-gradient-to-br bg-[#2ab8ff] flex items-center justify-center mb-4 text-[#141826]">
                {" "}
                {/* */}
                {feature.icon}
              </div>
              <h3 className="text-xl font-semibold mb-3">{feature.title}</h3>
              <p className="text-gray-400">{feature.description}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

//////////////////////////////////
// EXPORTING FEATURES
//////////////////////////////////

export default Features;
